import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const FIELDS = [
  ["full_name", "Full Name", "text"], ["fe_id", "FE ID", "text"],
  ["mobile", "Mobile", "text"], ["alt_mobile", "Alt Mobile", "text"],
];

export function EditDPDialog({ dp, open, onClose, onSaved, locs = [] }) {
  const { role } = useApp();
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (dp) setF({
      full_name: dp.full_name || "", fe_id: dp.fe_id || "", mobile: dp.mobile || "", alt_mobile: dp.alt_mobile || "",
      platform: dp.platform || "Zomato", location: dp.location || "", status: dp.status || "Active",
      parent_name: dp.parent_name || "", gender: dp.gender || "Male", dob: (dp.dob || "").slice(0, 10),
      address: dp.address || "", city: dp.city || "", state: dp.state || "", pincode: dp.pincode || "",
      aadhaar: dp.aadhaar || "", pan: dp.pan || "", dl: dp.dl || "",
      bank_account: dp.bank_account || "", ifsc: dp.ifsc || "", account_holder: dp.account_holder || "",
      emergency_name: dp.emergency_name || "", emergency_relation: dp.emergency_relation || "", emergency_phone: dp.emergency_phone || "",
    });
  }, [dp]);

  const save = async () => {
    if (!f.full_name || !f.fe_id) { toast.error("Name and FE ID are required"); return; }
    setSaving(true);
    try {
      await api.put(`/dp/${dp.id}`, { ...f, _user: role, _reason: "Profile edit" });
      toast.success("Delivery Partner updated");
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="edit-dp-dialog">
        <DialogHeader><DialogTitle className="font-head">Edit Delivery Partner</DialogTitle></DialogHeader>

        <Section title="Basic Details" />
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label, type]) => (
            <div key={k}><Label>{label}{(k === "full_name" || k === "fe_id") && " *"}</Label>
              <Input type={type} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`edit-dp-${k}`} /></div>
          ))}
          <div><Label>Platform</Label>
            <Select value={f.platform} onValueChange={(v) => set("platform", v)}>
              <SelectTrigger data-testid="edit-dp-platform"><SelectValue /></SelectTrigger>
              <SelectContent>{["Zomato", "Swiggy", "Blinkit", "Zepto"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Location</Label>
            <Select value={f.location} onValueChange={(v) => set("location", v)}>
              <SelectTrigger data-testid="edit-dp-location"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{locs.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="edit-dp-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["Active", "Inactive", "Suspended"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        </div>

        <Section title="Personal" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Father/Mother Name</Label><Input value={f.parent_name || ""} onChange={(e) => set("parent_name", e.target.value)} /></div>
          <div><Label>Gender</Label>
            <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Date of Birth</Label><Input type="date" value={f.dob || ""} onChange={(e) => set("dob", e.target.value)} /></div>
          <div><Label>Pincode</Label><Input value={f.pincode || ""} onChange={(e) => set("pincode", e.target.value)} /></div>
          <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
          <div><Label>City</Label><Input value={f.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
          <div><Label>State</Label><Input value={f.state || ""} onChange={(e) => set("state", e.target.value)} /></div>
        </div>

        <Section title="KYC & Bank" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Aadhaar</Label><Input value={f.aadhaar || ""} onChange={(e) => set("aadhaar", e.target.value)} /></div>
          <div><Label>PAN</Label><Input value={f.pan || ""} onChange={(e) => set("pan", e.target.value)} /></div>
          <div><Label>Driving Licence</Label><Input value={f.dl || ""} onChange={(e) => set("dl", e.target.value)} /></div>
          <div><Label>Bank Account</Label><Input value={f.bank_account || ""} onChange={(e) => set("bank_account", e.target.value)} /></div>
          <div><Label>IFSC</Label><Input value={f.ifsc || ""} onChange={(e) => set("ifsc", e.target.value)} /></div>
          <div><Label>Account Holder</Label><Input value={f.account_holder || ""} onChange={(e) => set("account_holder", e.target.value)} /></div>
        </div>

        <Section title="Emergency Contact" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={f.emergency_name || ""} onChange={(e) => set("emergency_name", e.target.value)} /></div>
          <div><Label>Relation</Label><Input value={f.emergency_relation || ""} onChange={(e) => set("emergency_relation", e.target.value)} /></div>
          <div><Label>Phone</Label><Input value={f.emergency_phone || ""} onChange={(e) => set("emergency_phone", e.target.value)} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-edit-dp">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title }) => (
  <p className="mt-4 border-b border-border pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
);
